/**
 * OnboardingContext — holds the in-flight first-run wizard draft.
 *
 * The 7 onboarding steps share this provider. It carries the current step, the
 * chosen genie type, the business details and the assembled OnboardingDraft.
 * `useOnboarding()` throws if used outside the provider (guarded accessor).
 *
 * Backend PARKED — the Launch submit is a no-op stub elsewhere; this only holds
 * client draft state.
 */
import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
  GenieTypeChoice,
  OnboardingBusiness,
  OnboardingDraft,
  OnboardingStepId,
} from "@/types/voice-vision";
import type { OnboardingContextValue } from "@/types/voice-vision/context";

const EMPTY_BUSINESS: OnboardingBusiness = { name: "" };

const EMPTY_DRAFT: OnboardingDraft = {
  genieType: null,
  business: EMPTY_BUSINESS,
  built: false,
  tested: false,
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [step, setStep] = useState<OnboardingStepId>("welcome");
  const [genieType, setGenieTypeState] = useState<GenieTypeChoice | null>(null);
  const [business, setBusinessState] = useState<OnboardingBusiness>(EMPTY_BUSINESS);
  const [draft, setDraft] = useState<OnboardingDraft>(EMPTY_DRAFT);

  // Keep the assembled draft in sync when type/business change.
  const setGenieType = (next: GenieTypeChoice | null) => {
    setGenieTypeState(next);
    setDraft((d) => ({ ...d, genieType: next }));
  };

  const setBusiness = (next: OnboardingBusiness) => {
    setBusinessState(next);
    setDraft((d) => ({ ...d, business: next }));
  };

  const value = useMemo<OnboardingContextValue>(
    () => ({
      step,
      setStep,
      genieType,
      setGenieType,
      business,
      setBusiness,
      draft,
      setDraft,
    }),
    [step, genieType, business, draft],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

/** Guarded accessor — throws if used outside OnboardingProvider. */
export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return ctx;
};
